/**
 * TIC Comms — Root application component.
 *
 * Route structure:
 *   /login                                → LoginPage (public)
 *   /oauth/callback                       → OAuthCallbackPage (public, outside AuthGuard)
 *   /                                     → redirect to /inbox
 *   /inbox                                → InboxPage (aggregate — All Businesses)
 *   /inbox/:businessId                    → InboxPage (scoped to one business)
 *   /inbox/:businessId/conversations/:id  → ConversationPage
 *   /search/:businessId                   → SearchPage (full-text message search)
 *   /contacts                             → ContactsPage
 *   /contacts/:id                         → ContactProfilePage (T1.2)
 *   /pipeline/:businessId                 → PipelinePage (T2.2 — kanban by stage)
 *   /templates                            → TemplatesPage
 *   /settings                             → SettingsPage
 *   /admin                                → AdminPage (role-gated)
 */

import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { AuthGuard } from '@/components/layout/AuthGuard'
import { AppShell } from '@/components/layout/AppShell'
import { LoginPage } from '@/pages/auth/LoginPage'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { TICToaster } from '@/components/ui/Toast'

// ============================================================
// Lazy-loaded page chunks
// ============================================================

const InboxPage = lazy(() =>
  import('@/pages/inbox/InboxPage').then((m) => ({ default: m.InboxPage }))
)

const ConversationPage = lazy(() =>
  import('@/pages/inbox/ConversationPage').then((m) => ({ default: m.ConversationPage }))
)

const AdminPage = lazy(() =>
  import('@/pages/admin/AdminPage').then((m) => ({ default: m.AdminPage }))
)

const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)

const OAuthCallbackPage = lazy(() =>
  import('@/pages/oauth/OAuthCallbackPage').then((m) => ({ default: m.OAuthCallbackPage }))
)

const ContactsPage = lazy(() =>
  import('@/pages/contacts/ContactsPage').then((m) => ({ default: m.ContactsPage }))
)

const ContactProfilePage = lazy(() =>
  import('@/pages/contacts/ContactProfilePage').then((m) => ({ default: m.ContactProfilePage }))
)

const TemplatesPage = lazy(() =>
  import('@/pages/templates/TemplatesPage').then((m) => ({ default: m.TemplatesPage }))
)

const SearchPage = lazy(() =>
  import('@/pages/search/SearchPage').then((m) => ({ default: m.SearchPage }))
)

const PipelinePage = lazy(() =>
  import('@/pages/pipeline/PipelinePage').then((m) => ({ default: m.PipelinePage }))
)

const TasksPage = lazy(() =>
  import('@/pages/tasks/TasksPage').then((m) => ({ default: m.TasksPage }))
)

const MyWorkPage = lazy(() =>
  import('@/pages/my-work/MyWorkPage').then((m) => ({ default: m.MyWorkPage }))
)

const TeamPage = lazy(() =>
  import('@/pages/team/TeamPage').then((m) => ({ default: m.TeamPage }))
)

// ============================================================
// Loading fallback
// ============================================================

function PageLoader() {
  return (
    <div className="space-y-6 py-8">
      <div className="skeleton h-8 w-48 rounded-lg" />
      <div className="skeleton h-4 w-72 rounded-md" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="skeleton h-40 rounded-[1.25rem]" />
        <div className="skeleton h-40 rounded-[1.25rem]" />
      </div>
      <div className="skeleton h-64 rounded-[1.25rem]" />
    </div>
  )
}

// ============================================================
// Query client
// ============================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// ============================================================
// App
// ============================================================

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <TICToaster />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

              {/* Protected routes */}
              <Route element={<AuthGuard />}>
                <Route element={<AppShell />}>
                  {/* Root redirect */}
                  <Route index element={<Navigate to="/inbox" replace />} />

                  {/* Inbox & Conversations */}
                  <Route path="inbox" element={<InboxPage />} />
                  <Route path="inbox/:businessId" element={<InboxPage />} />
                  <Route path="inbox/:businessId/conversations/:id" element={<ConversationPage />} />

                  {/* Search */}
                  <Route path="search/:businessId" element={<SearchPage />} />

                  {/* CRM */}
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="contacts/:id" element={<ContactProfilePage />} />
                  <Route path="pipeline/:businessId" element={<PipelinePage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="my-work" element={<MyWorkPage />} />
                  <Route path="templates" element={<TemplatesPage />} />

                  {/* System */}
                  <Route path="team" element={<TeamPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="admin" element={<AdminPage />} />

                  {/* 404 */}
                  <Route path="*" element={
                    <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                      <h2 className="text-lg font-semibold text-text-primary mb-2">Page not found</h2>
                      <p className="text-sm text-text-secondary">The page you're looking for doesn't exist.</p>
                    </div>
                  } />
                </Route>
              </Route>
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
